/**
 * Environment Variables Utility
 * 
 * Future-proof abstraction layer over environment variables.
 * Supports migration between different build systems (CRA → Vite → Next.js → etc.)
 * without requiring changes to the entire codebase.
 */

// Ensure dotenv is loaded for Node.js environments
if (typeof process !== 'undefined' && !process.env.VITE_ENVIRONMENT && !process.env.REACT_APP_ENVIRONMENT) {
  try {
    require('dotenv').config();
  } catch (e) {
    // dotenv not available or already loaded
  }
}

// Type definitions for all environment variables
interface EnvConfig {
  // Build & Runtime Configuration
  NODE_ENV: 'development' | 'production' | 'test';
  BROWSER: boolean;
  
  // Server Configuration
  ENVIRONMENT: 'development' | 'staging' | 'production' | 'sandbox' | 'stagging-sandbox';
  SERVER_URL: string;
  SERVER_SANDBOX_URL: string;
  SERVER_TOKEN: string;
  CLIENT_TOKEN: string;
  
  // Firebase Configuration
  USE_FIREBASE_EMU: boolean;
  FIREBASE_EMU_HOST: string;
  FIREBASE_CONFIG_APIKEY: string;
  FIREBASE_CONFIG_AUTHDOMAIN: string;
  FIREBASE_CONFIG_DATABASEURL: string;
  FIREBASE_CONFIG_PROJECTID: string;
  FIREBASE_CONFIG_STORAGEBUCKET: string;
  FIREBASE_CONFIG_MESSAGINGSENDERID: string;
  FIREBASE_CONFIG_APPID: string;
  FIREBASE_CONFIG_MEASUREMENTID: string;
  FIREBASE_WEB_PUSH_CERTIFICATE: string;
  
  // Sentry Configuration
  SENTRY_DSN: string;
  DEBUG: boolean;
  
  // Facebook/WhatsApp Configuration
  FB_APP_ID: string;
  FACEBOOK_WA_BUSINESS_ID: string;
  FACEBOOK_WA_ACCESS_TOKEN: string;
  FACEBOOK_WA_CREATE_SESSION_UPLOADFILE: string;
  FACEBOOK_WA_UPLOADFILE_DATA: string;
  
  // Theme & UI Configuration
  THEME: string;
  THEME_NAME: string;
  THEME_DEMO: string;
  THEME_API_URL: string;
  PURCHASE_URL: string;
  PREVIEW_REACT_URL: string;
  
  // Application Settings
  REQUEST_TIMEOUT: string;
  CLIENT_LIMITMESSAGE: string;
  LOG_LEVEL: string;
  PHONE_NUMBER_WHITELIST_LIMIT: string;
  HOUR_LIMIT_NOTIFICATION: string;
  EXTEND_LIMIT_INTERVAL_NOTIFICATION: string;
  USE_SAMPLE_NOTIFICATIONS: string;
  
  // Miscellaneous
  SEVEN_DAYS_SUPPORT_EMAIL: string;
  HTML_TITLE: string;
  HTML_DESCRIPTION: string;
  HTML_IMAGEURL: string;
  PUBLIC_URL: string;

  // Test Mode Configuration
  TEST_MODE: boolean;
}

// Type declarations for Vite-injected environment variables
declare const __ENV__: Record<string, string> | undefined;

/**
 * Smart environment variable resolver that supports multiple prefixes
 * for backward compatibility and future migrations
 */
function getEnvVar(key: string): string {
  // Browser environment (Vite client-side) - use injected environment object
  if (typeof window !== 'undefined') {
    // Check for Vite-injected environment variables
    if (typeof __ENV__ !== 'undefined') {
      const viteValue = __ENV__[`VITE_${key}`];
      const reactValue = __ENV__[`REACT_APP_${key}`];
      if (viteValue !== undefined || reactValue !== undefined) {
        return viteValue || reactValue || '';
      }
    }
  }

  // Server-side (Node.js) environment
  if (typeof process !== 'undefined' && process.env) {
    const viteValue = process.env[`VITE_${key}`];
    const reactValue = process.env[`REACT_APP_${key}`];
    if (viteValue !== undefined || reactValue !== undefined) {
      return viteValue || reactValue || '';
    }
  }

  return '';
}

/**
 * Parse boolean environment variable with proper defaults
 */
function getBooleanEnvVar(key: string, defaultValue: boolean = false): boolean {
  const value = getEnvVar(key).toLowerCase();
  if (value === 'true' || value === '1') return true;
  if (value === 'false' || value === '0') return false;
  return defaultValue;
}

/**
 * Get environment variable with validation for required values
 */
function getRequiredEnvVar(key: string): string {
  const value = getEnvVar(key);
  if (!value) {
    console.warn(`Missing required environment variable: ${key}`);
  }
  return value;
}

/**
 * Get NODE_ENV from multiple sources with proper fallback
 */
function getNodeEnv(): 'development' | 'production' | 'test' {
  // Check browser-injected NODE_ENV first (from Vite define)
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV) {
    return process.env.NODE_ENV as 'development' | 'production' | 'test';
  }
  
  // Fallback to environment variable
  const nodeEnv = getEnvVar('NODE_ENV') || 'development';
  return nodeEnv as 'development' | 'production' | 'test';
}

/**
 * Centralized environment configuration object
 * All environment variables should be accessed through this object
 */
export const env: EnvConfig = {
  // Build & Runtime Configuration
  NODE_ENV: getNodeEnv(),
  BROWSER: typeof window !== 'undefined',
  
  // Server Configuration
  ENVIRONMENT: getEnvVar('ENVIRONMENT') as EnvConfig['ENVIRONMENT'] || 'development',
  SERVER_URL: getRequiredEnvVar('SERVER_URL'),
  SERVER_SANDBOX_URL: getEnvVar('SERVER_SANDBOX_URL'),
  SERVER_TOKEN: getRequiredEnvVar('SERVER_TOKEN'),
  CLIENT_TOKEN: getEnvVar('CLIENT_TOKEN'),
  
  // Firebase Configuration
  USE_FIREBASE_EMU: getBooleanEnvVar('USE_FIREBASE_EMU'),
  FIREBASE_EMU_HOST: getEnvVar('FIREBASE_EMU_HOST') || '127.0.0.1',
  FIREBASE_CONFIG_APIKEY: getRequiredEnvVar('FIREBASE_CONFIG_APIKEY'),
  FIREBASE_CONFIG_AUTHDOMAIN: getRequiredEnvVar('FIREBASE_CONFIG_AUTHDOMAIN'),
  FIREBASE_CONFIG_DATABASEURL: getRequiredEnvVar('FIREBASE_CONFIG_DATABASEURL'),
  FIREBASE_CONFIG_PROJECTID: getRequiredEnvVar('FIREBASE_CONFIG_PROJECTID'),
  FIREBASE_CONFIG_STORAGEBUCKET: getRequiredEnvVar('FIREBASE_CONFIG_STORAGEBUCKET'),
  FIREBASE_CONFIG_MESSAGINGSENDERID: getRequiredEnvVar('FIREBASE_CONFIG_MESSAGINGSENDERID'),
  FIREBASE_CONFIG_APPID: getRequiredEnvVar('FIREBASE_CONFIG_APPID'),
  FIREBASE_CONFIG_MEASUREMENTID: getEnvVar('FIREBASE_CONFIG_MEASUREMENTID'),
  FIREBASE_WEB_PUSH_CERTIFICATE: getEnvVar('FIREBASE_WEB_PUSH_CERTIFICATE'),
  
  // Sentry Configuration
  SENTRY_DSN: getEnvVar('SENTRY_DSN'),
  DEBUG: getBooleanEnvVar('DEBUG', true),
  
  // Facebook/WhatsApp Configuration
  FB_APP_ID: getEnvVar('FB_APP_ID'),
  FACEBOOK_WA_BUSINESS_ID: getEnvVar('FACEBOOK_WA_BUSINESS_ID'),
  FACEBOOK_WA_ACCESS_TOKEN: getEnvVar('FACEBOOK_WA_ACCESS_TOKEN'),
  FACEBOOK_WA_CREATE_SESSION_UPLOADFILE: getEnvVar('FACEBOOK_WA_CREATE_SESSION_UPLOADFILE'),
  FACEBOOK_WA_UPLOADFILE_DATA: getEnvVar('FACEBOOK_WA_UPLOADFILE_DATA'),
  
  // Theme & UI Configuration
  THEME: getEnvVar('THEME') || 'sevendays',
  THEME_NAME: getEnvVar('THEME_NAME') || '7Days',
  THEME_DEMO: getEnvVar('THEME_DEMO'),
  THEME_API_URL: getEnvVar('THEME_API_URL') || getEnvVar('SERVER_URL'),
  PURCHASE_URL: getEnvVar('PURCHASE_URL'),
  PREVIEW_REACT_URL: getEnvVar('PREVIEW_REACT_URL'),
  
  // Application Settings
  REQUEST_TIMEOUT: getEnvVar('REQUEST_TIMEOUT') || '30000',
  CLIENT_LIMITMESSAGE: getEnvVar('CLIENT_LIMITMESSAGE') || '10',
  LOG_LEVEL: getEnvVar('LOG_LEVEL') || 'info',
  PHONE_NUMBER_WHITELIST_LIMIT: getEnvVar('PHONE_NUMBER_WHITELIST_LIMIT') || '20',
  HOUR_LIMIT_NOTIFICATION: getEnvVar('HOUR_LIMIT_NOTIFICATION') || '24',
  EXTEND_LIMIT_INTERVAL_NOTIFICATION: getEnvVar('EXTEND_LIMIT_INTERVAL_NOTIFICATION') || '24',
  USE_SAMPLE_NOTIFICATIONS: getEnvVar('USE_SAMPLE_NOTIFICATIONS') || 'false',
  
  // Miscellaneous
  SEVEN_DAYS_SUPPORT_EMAIL: getEnvVar('7DAYS_SUPPORT_EMAIL') || 'support@smile-interactive.com',
  HTML_TITLE: getEnvVar('HTML_TITLE') || '7Days App',
  HTML_DESCRIPTION: getEnvVar('HTML_DESCRIPTION') || '7Days Business Management Platform',
  HTML_IMAGEURL: getEnvVar('HTML_IMAGEURL') || '/logo.png',
  PUBLIC_URL: getEnvVar('PUBLIC_URL') || '',

  // Test Mode Configuration
  TEST_MODE: getBooleanEnvVar('TEST_MODE', false),
};

/**
 * Environment-specific helper flags
 */
export const isDevelopment = env.ENVIRONMENT === 'development';
export const isProduction = env.ENVIRONMENT === 'production';
export const isStaging = env.ENVIRONMENT === 'staging';
export const isSandbox = env.ENVIRONMENT === 'sandbox' || env.ENVIRONMENT === 'stagging-sandbox';

/**
 * Build-time environment helpers
 */
export const isDevBuild = env.NODE_ENV === 'development';
export const isProdBuild = env.NODE_ENV === 'production';
export const isTestBuild = env.NODE_ENV === 'test';
export const isBrowser = env.BROWSER;

/**
 * Test mode detection - checks localStorage for test mode flag
 *
 * Test mode enables:
 * - Service Registry mocks (MockAccountService, etc.)
 * - HTTP-level request mocking via Playwright
 * - Faster E2E test execution
 * - Predictable test data
 *
 * Set via Playwright init script: localStorage.setItem('USE_MOCKS', 'true')
 */
export function isTestMode(): boolean {
  // Check localStorage for USE_MOCKS flag (set by Playwright tests)
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    const useMocks = localStorage.getItem('USE_MOCKS')
    return useMocks === 'true' || useMocks === '1'
  }

  // Fallback to environment variable (build-time only)
  return getBooleanEnvVar('USE_MOCKS', false)
}

/**
 * Debug mode flag
 */
export const enableDebugMode = env.DEBUG && isDevelopment;

/**
 * Server URL selector based on environment
 */
export const getServerUrl = (): string => {
  return isSandbox ? env.SERVER_SANDBOX_URL : env.SERVER_URL;
};

/**
 * Validate required environment variables at startup
 */
export function validateEnvironment(): void {
  const required = [
    'SERVER_URL',
    'SERVER_TOKEN',
    'FIREBASE_CONFIG_APIKEY',
    'FIREBASE_CONFIG_AUTHDOMAIN',
    'FIREBASE_CONFIG_DATABASEURL',
    'FIREBASE_CONFIG_PROJECTID',
    'FIREBASE_CONFIG_STORAGEBUCKET',
    'FIREBASE_CONFIG_MESSAGINGSENDERID',
    'FIREBASE_CONFIG_APPID'
  ];
  
  const missing = required.filter(key => {
    const value = env[key as keyof EnvConfig];
    return !value || value === '';
  });
  
  if (missing.length > 0) {
    console.error(`Missing required environment variables: ${missing.join(', ')}`);
    console.error('Please check your .env file and ensure all required variables are set.');
    
    // Log what we actually have for debugging
    if (isDevelopment) {
      console.log('Current environment values:');
      required.forEach(key => {
        const value = env[key as keyof EnvConfig];
        console.log(`  ${key}: ${value ? '[SET]' : '[MISSING]'}`);
      });
    }
    
    if (isProduction) {
      throw new Error(`Missing required environment variables: ${missing.join(', ')}`);
    }
  }
}

/**
 * Firebase configuration object
 */
export const firebaseConfig = {
  apiKey: env.FIREBASE_CONFIG_APIKEY,
  authDomain: env.FIREBASE_CONFIG_AUTHDOMAIN,
  databaseURL: env.FIREBASE_CONFIG_DATABASEURL,
  projectId: env.FIREBASE_CONFIG_PROJECTID,
  storageBucket: env.FIREBASE_CONFIG_STORAGEBUCKET,
  messagingSenderId: env.FIREBASE_CONFIG_MESSAGINGSENDERID,
  appId: env.FIREBASE_CONFIG_APPID,
  measurementId: env.FIREBASE_CONFIG_MEASUREMENTID,
};

/**
 * Development utilities
 */
if (isDevelopment) {
  // Log environment info in development
  console.log('Environment Configuration:', {
    environment: env.ENVIRONMENT,
    serverUrl: getServerUrl(),
    debug: env.DEBUG,
    firebaseEmu: env.USE_FIREBASE_EMU,
  });
  
  // Only validate environment in Node.js context (not in browser)
  if (typeof process !== 'undefined' && process.env && !process.env.BROWSER) {
    validateEnvironment();
  }
}

export default env;